import { IMeta } from '@bees-web/core/types'
import { ChangeEvent, MouseEvent } from 'react'
import {
  rateMyDeliveryChange,
  rateMyDeliveryResetState,
  rateMyDeliverySubmitDismiss,
  rateMyDeliverySubmitRating,
  rateMyServiceChange,
  rateMyServiceRedirectUrl,
  rateMyServiceResetState,
  rateMyServiceSubmitDismiss,
  rateMyServiceSubmitRating,
  trackDeliveryRatingSubmission,
} from '../../actions/ratings'
import {
  trackServiceRatingSubmission,
  trackServiceRatingSuccessInteracted,
} from '../../actions/ratings/segment/RateMyService'
import {
  RATE_MY_DELIVERY_SUBMITTED,
  RATE_MY_SERVICE_LOAD_FAILED,
  RATE_MY_SERVICE_SUBMITTED,
} from '../../constants/ratings'
import { InteractiveMap } from '../../interfaces'
import {
  defineEntryMethodAndScreen,
  getAnswersResponse,
  getAnswerTypes,
  getSubmitButtonStatus,
} from '../../utils/ratingsUtils'
import {
  rateMyDelivery,
  rateMyDeliveryCommentTitle,
  rateMyDeliverySubmittedThankYouMessage,
  rateMyDeliverySurvey,
  rateMyDeliveryVendorName,
} from '../ratings/rateMyDelivery'
import { rateMyService } from '../ratings/rateMyService'
import {
  rateMyServiceQuestions,
  rateMyServiceQuestionsStars,
} from './rateMyServiceQuestions'

export const ratingsInteractive: InteractiveMap = (
  dispatch,
  interactiveState,
  globalActions
) => ({
  rate_my_delivery: rateMyDelivery(dispatch, interactiveState, globalActions),
  rate_my_delivery_dismiss: {
    attributes: {
      onClick: (e: MouseEvent<HTMLButtonElement>, meta: IMeta) => {
        e.preventDefault()
        rateMyDeliverySubmitDismiss(
          e,
          meta,
          interactiveState,
          globalActions,
          dispatch
        )
      },
    },
  },
  rate_my_delivery_vendor_name: rateMyDeliveryVendorName(
    dispatch,
    interactiveState
  ),
  rate_my_delivery_stars: {
    attributes: {
      onSaveRating: (score: number, meta: IMeta) => {
        if (interactiveState.state.ratings.rateMyDelivery.score === score)
          return

        dispatch(rateMyDeliveryChange({ score }))
        const { survey } = interactiveState.state.ratings.rateMyDelivery
        if (score === 5) {
          dispatch(
            rateMyDeliveryChange({ answers: [], mandatoryComment: false })
          )
        } else if (score < 5 && score > 0 && !survey.options) {
          dispatch(
            globalActions.requester.doRequest({
              ...meta.request,
              finally: ({ response, data }) => {
                const { content: survey } = data
                if (response.ok) {
                  dispatch(
                    rateMyDeliveryChange({ survey: survey[0].questions[0] })
                  )
                } else {
                  dispatch(rateMyDeliveryChange({ modalStatus: 'error' }))
                }
              },
            })
          )
        }
      },
    },
  },
  rate_my_delivery_feedback: {
    attributes: {
      visible:
        interactiveState.state.ratings.rateMyDelivery.score < 5 &&
        interactiveState.state.ratings.rateMyDelivery.score > 0,
    },
  },
  rate_my_delivery_survey_loading: {
    attributes: {
      visible:
        interactiveState.state.ratings.rateMyDelivery.score < 5 &&
        interactiveState.state.ratings.rateMyDelivery.score > 0 &&
        !interactiveState.state.ratings.rateMyDelivery.survey.options,
    },
  },
  rate_my_delivery_survey: rateMyDeliverySurvey(dispatch, interactiveState),
  rate_my_delivery_submit: {
    attributes: {
      disabled:
        (interactiveState.state.ratings.rateMyDelivery.score < 5 &&
          (interactiveState.state.ratings.rateMyDelivery.answers[0]?.responses
            .length === 0 ||
            typeof interactiveState.state.ratings.rateMyDelivery.answers[0] ===
              'undefined')) ||
        (interactiveState.state.ratings.rateMyDelivery.mandatoryComment &&
          interactiveState.state.ratings.rateMyDelivery.comment === ''),
      onClick: (e: MouseEvent<HTMLButtonElement>, meta: IMeta) => {
        const { deliveryDate, orderId, vendorId } = meta
        const { accountId } =
          interactiveState.globals.authentication.selectedAccount
        const { score, answers, comment } =
          interactiveState.state.ratings.rateMyDelivery
        dispatch(
          trackDeliveryRatingSubmission({
            delivery_date: deliveryDate,
            order_id: orderId,
            vendorId: vendorId,
            poc_id: accountId,
            rating_given: score,
            native_language_rating_reason: comment,
            rating_free_text: comment,
            rating_reason: answers[0]?.responses.join(','),
            type: RATE_MY_DELIVERY_SUBMITTED,
          })
        )
        rateMyDeliverySubmitRating(
          e,
          meta,
          interactiveState,
          globalActions,
          dispatch
        )
      },
    },
  },
  rate_my_delivery_submitted_state: {
    attributes: {
      visible:
        interactiveState.state.ratings.rateMyDelivery.modalStatus ===
        'submitted',
    },
  },
  rate_my_delivery_submitted_thank_you_message:
    rateMyDeliverySubmittedThankYouMessage(dispatch, interactiveState),
  rate_my_delivery_error_state: {
    attributes: {
      visible:
        interactiveState.state.ratings.rateMyDelivery.modalStatus === 'error',
    },
  },
  rate_my_delivery_initial_state: {
    attributes: {
      visible:
        interactiveState.state.ratings.rateMyDelivery.modalStatus === 'initial',
    },
  },
  rate_my_delivery_comment_title: rateMyDeliveryCommentTitle(
    dispatch,
    interactiveState
  ),
  rate_my_delivery_comment: {
    attributes: {
      onChange: (value: string) => {
        dispatch(
          rateMyDeliveryChange({
            comment: value,
          })
        )
      },
    },
  },
  rate_my_delivery_close: {
    attributes: {
      onClick: () => {
        dispatch(rateMyDeliveryResetState)
      },
    },
  },
  rate_my_service: rateMyService(dispatch, interactiveState, globalActions),
  rate_my_service_dismiss: {
    attributes: {
      onClick: (e: MouseEvent<HTMLButtonElement>, meta: IMeta) => {
        e.preventDefault()
        rateMyServiceSubmitRating(
          e,
          meta,
          interactiveState,
          globalActions,
          dispatch,
          true
        )
        rateMyServiceSubmitDismiss(
          e,
          meta,
          interactiveState,
          globalActions,
          dispatch
        )
      },
    },
  },
  rate_my_service_survey: {
    attributes: {
      question: interactiveState.state.ratingService.rateMyService.survey,
      surveyType: 'SINGLE_CHOICE',
      radioAttributes: {
        labelClasses: 'is-margin-vertical-m is-justify-content-start',
        radioClasses: 'is-margin-right-m',
      },
      onChange: (
        _e: ChangeEvent<HTMLInputElement>,
        _meta: IMeta,
        newOption: { optionKey: string; optionValue: string }
      ) => {
        dispatch(
          rateMyServiceChange({
            isSubmitButtonDisabled: false,
          })
        )
        dispatch(
          rateMyServiceChange({
            answers: [
              {
                questionId:
                  interactiveState.state.ratingService.rateMyService.survey.id,
                responses: [newOption.optionKey],
              },
            ],
          })
        )
      },
    },
  },
  rate_my_service_survey_error: {
    attributes: {
      ates: interactiveState.state.ratingService.rateMyService.rmsResponse,
      visible: interactiveState.state.ratingService.rateMyService.surveyError,
    },
  },
  rate_my_service_stars: {
    attributes: {
      onSaveRating: (score: number, meta: IMeta) => {
        if (interactiveState.state.ratingService.rateMyService.score === score)
          return

        if (score > 3) {
          dispatch(rateMyServiceChange({ isVisibleQuestions: false }))
          dispatch(
            rateMyServiceChange({
              isSubmitButtonDisabled: false,
            })
          )
        } else {
          dispatch(rateMyServiceChange({ isVisibleQuestions: true }))
          const isSubmitButtonDisabled = getSubmitButtonStatus(
            interactiveState.state.ratingService.rateMyService
              .requiredQuestions,
            interactiveState.state.ratingService.rateMyService.answers
          )
          dispatch(
            rateMyServiceChange({
              isSubmitButtonDisabled: !isSubmitButtonDisabled,
            })
          )
        }
        dispatch(rateMyServiceChange({ score }))
        dispatch(rateMyServiceChange({ loadingFeedBack: true }))
        dispatch(
          globalActions.requester.doRequest({
            ...meta.request,
            finally: ({ response, data }) => {
              if (response.ok) {
                //limpeza de dados e criação de novo objeto de dados
                const { content } = data
                const parseContent = content.reduce((acc, config) => {
                  const {
                    questions: questionsConfig,
                    language,
                    ratingThreshold: ratingThresholdConfig,
                  } = config
                  if (language === 'en') {
                    return {
                      ...acc,
                      questions: acc.questions ?? questionsConfig,
                      questionsEnglish: questionsConfig,
                      ratingThresholdConfig,
                    }
                  }
                  return {
                    ...acc,
                    questions: questionsConfig,
                    ratingThresholdConfig,
                  }
                }, {})
                const { questions, ratingThresholdConfig, questionsEnglish } =
                  parseContent

                const optionsParse = questions[0].options
                  .map((option) => {
                    if (!questionsEnglish) return option

                    return {
                      ...option,
                      optionValueEnglish: questionsEnglish[0].options.find(
                        (englishOption) =>
                          englishOption.optionKey === option.optionKey
                      ).optionValue,
                    }
                  })
                  .sort((option1, option2) =>
                    option1.optionValue > option2.optionValue ? 1 : -1
                  )

                if (score > ratingThresholdConfig) {
                  dispatch(
                    rateMyServiceChange({
                      answers: [],
                      survey: {},
                      surveyError: false,
                      loadingFeedBack: false,
                      ratingThreshold: ratingThresholdConfig,
                    })
                  )
                } else {
                  dispatch(
                    rateMyServiceChange({
                      survey: { ...questions[0], options: optionsParse },
                      surveyError: false,
                      answers: [],
                      loadingFeedBack: false,
                      ratingThreshold: ratingThresholdConfig,
                    })
                  )
                }
              } else {
                //event_segment_response_fail
                const { authentication, user } = interactiveState.globals
                const { entry_method, step_name } = defineEntryMethodAndScreen()
                dispatch(
                  trackServiceRatingSubmission({
                    type: RATE_MY_SERVICE_LOAD_FAILED,
                    app_instance: user.country,
                    failure_reason: response.statusText,
                    poc_id: authentication.selectedAccount.accountId,
                    ticket_id: meta?.ticket?.content[0]?.useCaseId,
                    vendorId: meta?.ticket?.content[0]?.metadata?.vendorId,
                    rating_type:
                      interactiveState.state.ratingService.rateMyService
                        .rmsResponse.ratingType,
                    step_name: step_name,
                    entry_method: entry_method,
                  })
                )
                //event_segment
                dispatch(
                  rateMyServiceChange({
                    loadingFeedBack: false,
                    ratingThreshold: null,
                    survey: {},
                    surveyError: true,
                  })
                )
              }
            },
          })
        )
      },
    },
  },
  rate_my_service_stars_subtitle: {
    attributes: {
      style: {
        display: 'flex',
        'font-size': '0.75rem',
        'justify-content': 'center',
        'align-items': 'center',
        width: '100%',
      },
    },
    text: interactiveState.state.ratingService.rateMyService.subtitle,
  },
  rate_my_service_stars_no_stars_selected: {
    attributes: {
      style: {
        display:
          interactiveState.state.ratingService.rateMyService.score > 0
            ? 'none'
            : 'flex',
        width: '105%',
        justifyContent: 'space-between',
      },
    },
  },
  rate_my_service_feedback: {
    attributes: {
      visible:
        interactiveState.state.ratingService.rateMyService.isVisibleQuestions ??
        Object.keys(interactiveState.state.ratingService.rateMyService.survey)
          .length !== 0,
    },
  },
  rate_my_service_submitted_state: {
    attributes: {
      visible:
        interactiveState.state.ratingService.rateMyService.modalStatus ===
        'submitted',
    },
  },
  rate_my_service_modal_show: {
    attributes: {
      style: { margin: '-20px -40px' },
    },
  },
  rate_my_service_error_state: {
    attributes: {
      visible:
        interactiveState.state.ratingService.rateMyService.modalStatus ===
        'error',
    },
  },
  rate_my_service_close: {
    attributes: {
      onClick: () => {
        dispatch(rateMyServiceResetState)
      },
    },
  },
  rate_my_service_rate_other_request: {
    attributes: {
      onClick: (e: MouseEvent<HTMLButtonElement>, meta: IMeta) => {
        dispatch(rateMyServiceRedirectUrl(meta.href))

        const { user, authentication } = interactiveState.globals
        const rmsResponseState =
          interactiveState.state.ratingService.rateMyService.rmsResponse
        const answerTypeList = getAnswerTypes(rmsResponseState.questions)
        const { entry_method, screen_name } = defineEntryMethodAndScreen()
        dispatch(
          trackServiceRatingSuccessInteracted({
            app_instance: user.country,
            poc_id: authentication.selectedAccount.accountId,
            screen_name: screen_name,
            rating_type: rmsResponseState.ratingType,
            answer_type: answerTypeList,
            number_of_questions: rmsResponseState.questions?.length,
            rating_sucess_action: 'Request history button',
            entry_method: entry_method,
          })
        )
      },
    },
  },
  rate_my_service_success_modal_close_button: {
    attributes: {
      onClick: () => {
        const { user, authentication } = interactiveState.globals
        const rmsResponseState =
          interactiveState.state.ratingService.rateMyService.rmsResponse
        const answerTypeList = getAnswerTypes(rmsResponseState.questions)
        const { entry_method, screen_name } = defineEntryMethodAndScreen()

        dispatch(rateMyServiceResetState)

        dispatch(
          trackServiceRatingSuccessInteracted({
            app_instance: user.country,
            poc_id: authentication.selectedAccount.accountId,
            screen_name: screen_name,
            rating_type: rmsResponseState.ratingType,
            answer_type: answerTypeList,
            number_of_questions: rmsResponseState.questions?.length,
            rating_sucess_action: 'Close button',
            entry_method: entry_method,
          })
        )
      },
    },
  },
  rate_my_service_comment: {
    attributes: {
      onChange: (value: string) => {
        dispatch(
          rateMyServiceChange({
            comment: value,
          })
        )
      },
    },
  },
  rate_my_service_submit: {
    attributes: {
      disabled:
        interactiveState.state.ratingService.rateMyService
          .isSubmitButtonDisabled,
      onClick: (e: MouseEvent<HTMLButtonElement>, meta: IMeta) => {
        const { score, comment, answers } =
          interactiveState.state.ratingService.rateMyService
        const { authentication, user } = interactiveState.globals || null
        const rmsResponseState =
          interactiveState.state.ratingService.rateMyService.rmsResponse
        const answerTypeList = getAnswerTypes(rmsResponseState.questions)
        const answersList = getAnswersResponse(answers)
        const { vendorId, ticketId } = meta
        const { entry_method, screen_name } = defineEntryMethodAndScreen()
        dispatch(
          trackServiceRatingSubmission({
            type: RATE_MY_SERVICE_SUBMITTED,
            app_instance: user.country,
            poc_id: authentication.selectedAccount.accountId,
            rating_free_text: comment,
            rating_given: score,
            screen_name: screen_name,
            vendorId: vendorId,
            ticket_id: ticketId,
            rating_type: rmsResponseState.ratingType,
            answer_type: answerTypeList,
            number_of_questions: rmsResponseState.questions?.length,
            question_responses: answersList,
            entry_method: entry_method,
          })
        )
        rateMyServiceSubmitRating(
          e,
          meta,
          interactiveState,
          globalActions,
          dispatch
        )
      },
    },
  },
  rate_my_service_initial_state: {
    attributes: {
      visible:
        interactiveState.state.ratingService.rateMyService.modalStatus ===
        'initial',
    },
  },
  rate_my_service_questions: rateMyServiceQuestions(
    dispatch,
    interactiveState,
    globalActions
  ),
  rate_my_service_questions_stars: rateMyServiceQuestionsStars(
    dispatch,
    interactiveState,
    globalActions
  ),
  rate_my_service_version_prod: {
    attributes: {
      visible:
        interactiveState.state.ratingService.rateMyService.isShowOldModal,
    },
  },
  rate_my_service_version_sit: {
    attributes: {
      style: {
        position: 'relative',
        height: window.innerHeight < 862 ? '448px' : 'auto',
      },
      visible:
        !interactiveState.state.ratingService.rateMyService.isShowOldModal,
    },
  },
  rate_my_service_button_container: {
    attributes: {
      style: {
        position: 'fixed',
        bottom: window.innerHeight < 862 ? '5px' : '20px',
        marginRight: '20px',
      },
    },
  },
  rate_my_service_divider: {
    attributes: {
      style: {
        width: '2px',
        height:
          window.innerHeight < 862
            ? 'calc(100dvh - 300px)'
            : 'calc(100dvh - 400px)',
        margin: '0px 20px 0 10px',
      },
    },
  },
})
